import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Plus, Video, VolumeX } from 'lucide-react'
import { Button, EmptyState, ToggleGroup, type ToggleGroupItem } from '@/components/play'
import type { StreamResource } from '@/types/events'
import { useCameraState } from './WorkspaceMedia'
import { WorkspaceVideo } from './WorkspaceVideo'
import { WorkspaceInfo } from '@/components/workspace/WorkspaceInfo'
import './multiview.css'

export type MultiviewLayout = 'auto' | 1 | 2 | 3 | 4

type LayoutValue = 'auto' | '1' | '2' | '3' | '4'

const LAYOUT_ITEMS: ToggleGroupItem<LayoutValue>[] = [
  { value: 'auto', label: 'Auto', accessibleLabel: 'Automatic columns' },
  { value: '1', label: '1', accessibleLabel: '1 column' },
  { value: '2', label: '2', accessibleLabel: '2 columns' },
  { value: '3', label: '3', accessibleLabel: '3 columns' },
  { value: '4', label: '4', accessibleLabel: '4 columns' },
]

const toLayout = (value: LayoutValue): MultiviewLayout => (value === 'auto' ? 'auto' : (Number(value) as 1 | 2 | 3 | 4))

export interface WorkspaceMultiviewProps {
  cameras: StreamResource[]
  programme?: StreamResource | null
  selectedId?: string | null
  featuredIdentity?: string | null
  layout: MultiviewLayout
  onLayoutChange: (layout: MultiviewLayout) => void
  onSelect: (id: string) => void
  onPreview: (stream: StreamResource) => void
  onAddCamera: () => void
  onVisibilityChange?: (stream: StreamResource, visible: boolean) => Promise<void>
  onDetails?: (stream: StreamResource) => void
  onFeature?: (stream: StreamResource) => void | Promise<void>
  featureBusy?: boolean
  publicEnabled?: boolean
  onSetup?: () => void
}

function MultiviewTile({
  stream,
  number,
  selected,
  featured,
  onSelect,
  onPreview,
  onDetails,
  onVisibilityChange,
  onFeature,
  featureBusy,
  publicEnabled = true,
  onSetup,
}: {
  stream: StreamResource
  number?: number
  selected: boolean
  featured: boolean
  onSelect: () => void
  onPreview: () => void
  onDetails?: () => void
  onVisibilityChange?: (visible: boolean) => Promise<void>
  onFeature?: () => void | Promise<void>
  featureBusy?: boolean
  publicEnabled?: boolean
  onSetup?: () => void
}) {
  const state = useCameraState(stream)
  const isShow = stream.attributes.source_role === 'programme'
  const name = isShow ? 'Live show' : stream.attributes.name
  const publicSource = stream.attributes.publicly_visible !== false
  const canFeature = featured || (publicEnabled && publicSource && !!stream.attributes.participant_identity && (!isShow || state === 'live'))
  const featureDisabledReason = !publicEnabled
    ? 'Enable the public event page in event settings.'
    : !publicSource
      ? `Make this ${isShow ? 'live show' : 'camera'} public before featuring it.`
      : !stream.attributes.participant_identity
        ? 'This source is not ready to feature yet.'
        : 'Start streaming in OBS before featuring this show.'

  return (
    <article
      className="pw-multiview-cell"
      aria-label={name}
      data-source-role={isShow ? 'programme' : 'camera'}
      data-camera-id={stream.id}
    >
      <WorkspaceVideo
        stream={stream}
        number={number}
        selected={selected}
        featured={featured}
        showName
        onSelect={onSelect}
        onPreview={onPreview}
        onDetails={onDetails}
        onVisibilityChange={onVisibilityChange}
        onFeature={onFeature}
        featureBusy={featureBusy}
        canFeature={canFeature}
        featureDisabledReason={featureDisabledReason}
        onSetup={onSetup}
      />
    </article>
  )
}

export function WorkspaceMultiview({
  cameras,
  programme,
  selectedId,
  featuredIdentity,
  layout,
  onLayoutChange,
  onSelect,
  onPreview,
  onAddCamera,
  onVisibilityChange,
  onDetails,
  onFeature,
  featureBusy,
  publicEnabled,
  onSetup,
}: WorkspaceMultiviewProps) {
  const container = useRef<HTMLElement>(null)
  const [containerWidth, setContainerWidth] = useState<number | null>(null)
  useEffect(() => {
    const element = container.current
    if (!element) return
    const width = element.getBoundingClientRect().width
    if (width > 0) setContainerWidth(width)
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      if (entry?.contentRect.width > 0) setContainerWidth(entry.contentRect.width)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const maximumColumns = containerWidth !== null && containerWidth <= 880 ? 2 : 4
  const sourceCount = cameras.length + (programme ? 1 : 0)
  const automaticColumns = sourceCount <= 1 ? 1 : sourceCount <= 4 ? 2 : sourceCount <= 9 ? 3 : 4
  const columns = Math.min(sourceCount || 1, maximumColumns, layout === 'auto' ? automaticColumns : layout)
  const visibleLayout = layout === 'auto' ? 'auto' : Math.min(layout, maximumColumns)
  const layoutItems = LAYOUT_ITEMS.slice(0, maximumColumns + 1)

  if (sourceCount === 0) {
    return (
      <section ref={container} className="pw-multiview" aria-label="Camera multiview">
        <EmptyState
          className="pw-multiview-empty"
          icon={<Video />}
          title="Your cameras, together."
          description="Add a camera to start your multiview."
          action={
            <Button onClick={onAddCamera}>
              <Plus aria-hidden />
              Add camera
            </Button>
          }
        />
      </section>
    )
  }

  return (
    <section ref={container} className="pw-multiview" aria-label="Camera multiview">
      <div className="pw-multiview-toolbar">
        <WorkspaceInfo icon={VolumeX} label="About muted previews">
          Multiview previews are muted. Expand a video to listen.
        </WorkspaceInfo>
        <ToggleGroup
          label="Columns"
          accessibleLabel="Multiview columns"
          items={layoutItems}
          value={String(visibleLayout) as LayoutValue}
          onValueChange={(value) => onLayoutChange(toLayout(value))}
          className="pw-multiview-layout"
        />
      </div>
      <div
        className="pw-multiview-wall"
        data-columns={columns}
        style={{ '--multiview-columns': columns } as CSSProperties}
      >
        {programme && (
          <MultiviewTile
            stream={programme}
            selected={selectedId === programme.id}
            featured={!!featuredIdentity && programme.attributes.participant_identity === featuredIdentity}
            onSelect={() => onSelect(programme.id)}
            onPreview={() => onPreview(programme)}
            onVisibilityChange={onVisibilityChange && ((visible) => onVisibilityChange(programme, visible))}
            onFeature={onFeature && (() => onFeature(programme))}
            featureBusy={featureBusy}
            publicEnabled={publicEnabled}
            onSetup={onSetup}
          />
        )}
        {cameras.map((camera, index) => (
          <MultiviewTile
            key={camera.id}
            stream={camera}
            number={index + 1}
            selected={selectedId === camera.id}
            featured={!!featuredIdentity && camera.attributes.participant_identity === featuredIdentity}
            onSelect={() => onSelect(camera.id)}
            onPreview={() => onPreview(camera)}
            onVisibilityChange={onVisibilityChange && ((visible) => onVisibilityChange(camera, visible))}
            onDetails={onDetails && (() => onDetails(camera))}
            onFeature={onFeature && (() => onFeature(camera))}
            featureBusy={featureBusy}
            publicEnabled={publicEnabled}
          />
        ))}
      </div>
    </section>
  )
}
